import type React from 'react';
import { useContext, useMemo, useState } from 'react';

import { Link_Target } from './../../../ricos-schema/legacy/wix/rich_content/v1';
import { useBiNext } from './../../bi-context-provider/bi-context-provider';
import { EditorBaseUrlContext } from './../../editor-base-url/editor-base-url-context';
import { isPageLink } from './../../link/is-page-link';
import type { NamedLink } from './../../link/types';
import { getEditableUrl } from './../../url/get-editable-url';
import { getHrefUrl } from './../../url/get-href-url';

import { LINK_MODAL_ID } from '../constants';
import { SAVE_BUTTON } from '../data-hooks/data-hooks';
import type { LinkModalProps, LinkType, SubmitError } from './types';

type FocusTarget = 'type-select' | 'input';

type HookResult = {
  focusTarget: FocusTarget;
  link: NamedLink;
  linkType: LinkType;
  submitError: SubmitError | null;
  multipleSections: NamedLink[];

  onChangeAttributes: (rel: NamedLink['rel'], target: NamedLink['target']) => void;
  onChangeWebAddress: (url: string | undefined) => void;
  onClickUnlink: () => void;
  onSelectPage: (path: string, title: string) => void;
  onSelectSection: (anchor: string, defaultName: string | undefined) => void;
  onSelectType: (newLinkType: LinkType) => void;
  onSubmit: (evt?: React.FormEvent) => void;
  onValidateWebAddress: (url: string) => void;
  setMultipleSections: React.Dispatch<React.SetStateAction<NamedLink[]>>;
};

const defaultValue: NamedLink = {
  anchor: undefined,
  rel: {
    noreferrer: true,
  },
  target: Link_Target.BLANK,
  url: '',
};

export const useLinkModalState = ({
  initialValue,
  onClose,
  onSave,
  onUnlink,
  onSaveMultiple,
}: LinkModalProps): HookResult => {
  const { baseUrl } = useContext(EditorBaseUrlContext);
  const logBi = useBiNext({ pluginId: 'link', modalId: LINK_MODAL_ID });

  const normalizedInitialValue = useMemo(() => {
    let link = initialValue || defaultValue;
    if (baseUrl && isPageLink(link)) {
      link = { ...link, url: getEditableUrl(link.url as string, baseUrl) };
    }
    return link;
  }, []);

  const [focusTarget, setFocusTarget] = useState<FocusTarget>(() =>
    initialValue?.url || initialValue?.anchor ? 'input' : 'type-select'
  );
  const [linkType, setLinkType] = useState<LinkType>(
    initialValue?.anchor ? 'section' : 'web-address'
  );
  const [link, setLink] = useState<NamedLink>(normalizedInitialValue);
  const [multipleSections, setMultipleSections] = useState<NamedLink[]>([]);
  const [submitError, setSubmitError] = useState<SubmitError | null>(null);

  const onChangeAttributes = (rel: NamedLink['rel'], target: NamedLink['target']) => {
    setLink(data => ({
      ...data,
      rel,
      target,
    }));
  };
  const onChangeWebAddress = (url: string | undefined) => {
    setLink(data =>
      data.url === url
        ? data
        : {
            ...data,
            anchor: undefined,
            url,
          }
    );
    setSubmitError(null);
  };
  const onClickUnlink = () => {
    if (initialValue) {
      onUnlink(initialValue);
    }
    onClose();
  };
  const onSelectPage = (path: string, title: string) => {
    setLink(data => ({
      ...data,
      anchor: undefined,
      defaultName: title,
      url: path,
    }));
  };
  const onSelectSection = (anchor: string, defaultName: string | undefined) => {
    setLink(data => ({
      ...data,
      anchor,
      defaultName,
      url: undefined,
    }));
  };

  const onSelectType = (newLinkType: LinkType) => {
    if (newLinkType !== linkType) {
      setLink(normalizedInitialValue);
      setFocusTarget('type-select');
      setSubmitError(null);
    }
    setLinkType(newLinkType);
  };
  const onSubmit = (evt?: React.FormEvent) => {
    if (multipleSections.length > 0 && linkType === 'section') {
      onSaveMultiple?.(multipleSections);
      onClose();
    } else if (!link.anchor && !link.url) {
      onClickUnlink();
    } else if (link.url && !getHrefUrl(link.url, baseUrl)) {
      evt?.preventDefault();
      setFocusTarget('input');
      setSubmitError('invalid-url');
    } else {
      onSave(
        link.anchor
          ? {
              anchor: link.anchor,
              defaultName: link.defaultName,
              target: Link_Target.SELF,
              url: undefined,
            }
          : {
              anchor: undefined,
              defaultName: link.defaultName,
              rel:
                link.rel && Object.values(link.rel).filter(v => v !== undefined).length
                  ? link.rel
                  : undefined,
              target: link.target,
              url: link.url,
            }
      );
      onClose();
    }
    logBi({ dataHook: SAVE_BUTTON });
  };
  const onValidateWebAddress = (url: string) => {
    setSubmitError(url && !getHrefUrl(url, baseUrl) ? 'invalid-url' : null);
  };

  return {
    focusTarget,
    link,
    linkType,
    submitError,
    multipleSections,

    onChangeAttributes,
    onChangeWebAddress,
    onClickUnlink,
    onSelectPage,
    onSelectSection,
    setMultipleSections,
    onSelectType,
    onSubmit,
    onValidateWebAddress,
  };
};
